const post = {
  slug: "meesho-amazon-or-instagram-where-to-sell",
  title: "Meesho, Amazon or Instagram: Where Should a Small Seller Sell?",
  seoTitle: "Meesho vs Amazon vs Instagram for Sellers",
  description:
    "Meesho, Amazon or Instagram for a small Indian seller? Compare fees, GST rules, customers and control, see a worked cost example and learn how to combine them.",
  excerpt:
    "Marketplaces bring buyers but take a cut in fees, shipping and rules. Instagram brings your own audience. Here is how Meesho, Amazon and Instagram compare for a small Indian seller.",
  category: "Comparisons",
  tags: ["Meesho vs Amazon vs Instagram", "Where to sell online India", "Checkout DM vs marketplaces"],
  date: "2026-11-05",
  cover: {
    src: "/blog-images/meesho-amazon-or-instagram-where-to-sell-cover.png",
    alt: "A phone showing an Instagram DM order from a customer, next to marketplace logos",
  },
  blocks: [
    {
      type: "p",
      text:
        "If you are starting to sell online in India, three options come up again and again: Meesho, Amazon and Instagram. Each can work, but they work very differently. Marketplaces bring you buyers and take a share of every sale in fees and rules. Instagram brings you nothing until you build an audience, but you keep control.",
    },
    {
      type: "p",
      text:
        "This guide compares them for a small seller, with a worked cost example. The marketplace figures come from several 2026 seller guides, because neither marketplace publishes every number openly, so please check the current fees on each seller portal before you decide.",
    },

    { type: "h2", text: "How the three work" },
    {
      type: "p",
      text:
        "**Meesho** is a marketplace known for budget fashion, home and lifestyle products, with many buyers in smaller towns. Guides report that it charges no commission and no listing fee, and earns from shipping, returns and ads instead. Many guides also report that GST registration is not required to start in most categories.",
    },
    {
      type: "p",
      text:
        "**Amazon** is a larger marketplace with a broader, often more premium, set of buyers. Sellers pay fees that vary by category, such as a referral fee, plus other charges like closing and shipping fees, and GST registration is generally required. Amazon changes its fee schedule from time to time, so use its own fee calculator.",
    },
    {
      type: "p",
      text:
        "**Instagram** is not a marketplace. You build an audience with posts, Reels and Stories and take orders through DMs, comments and links. With Checkout DM, the catalog, size choice, address and payment happen inside the chat, and Checkout DM does not take a commission on your orders.",
    },

    { type: "h2", text: "Side by side" },
    {
      type: "table",
      headers: ["", "Meesho", "Amazon", "Instagram with Checkout DM"],
      rows: [
        ["Who finds you", "Marketplace shoppers", "Marketplace shoppers", "Your followers and your content\u2019s reach"],
        ["Commission", "Reported as 0%", "Varies by category", "None from Checkout DM"],
        ["Other costs", "Shipping on each order, return shipping, penalties", "Closing, shipping and other fees", "Your plan, your courier and your payment gateway"],
        ["GST registration", "Reported as optional for most categories", "Generally required", "Depends on your own turnover and products"],
        ["Pricing pressure", "High, buyers compare on price", "Moderate to high", "You set the price"],
        ["Customer relationship", "The marketplace owns it", "The marketplace owns it", "You own it"],
        ["Repeat customers", "Hard to reach again", "Hard to reach again", "They are your followers"],
      ],
    },

    { type: "h2", text: "A worked cost example" },
    {
      type: "p",
      text:
        "Say you sell a kurti at \u20B9300 and the customer pays for it. Seller guides in 2026 report that a light fashion parcel on Meesho commonly costs around \u20B965 in shipping, with 18% GST on that fee. That is about \u20B977 in total, or roughly a quarter of the price, taken as a fee for the delivery. If the customer returns the order, guides report a return shipping charge of roughly \u20B9150 to \u20B9165 on top.",
    },
    {
      type: "p",
      text:
        "Shipping costs money on every platform. On Instagram you pay your own courier, so the difference is not that shipping is free but who sets the price, who handles returns and how much of the margin you keep. Our guide to [pricing products on Instagram](/blog/price-products-on-instagram-india) shows how to build every cost into the price, and [packing and shipping Instagram orders](/blog/pack-and-ship-instagram-orders) covers courier choices.",
    },
    {
      type: "callout",
      tone: "peach",
      title: "Compare net money, not headline fees",
      text:
        "A platform that charges nothing on paper can still cost a lot once shipping, returns and penalties are added. Work out what actually reaches your bank on one real product before you choose.",
    },

    { type: "h2", text: "Where marketplaces are stronger" },
    {
      type: "ul",
      items: [
        "**Built-in buyers.** People already shop there, so a good listing can sell without an audience.",
        "**Fast start.** Registration and listing are structured, so you can go live quickly.",
        "**Logistics and trust.** Shipping, tracking and payment handling are part of the platform.",
        "**Volume.** For low-price products with thin margins, scale can matter more than control.",
      ],
    },

    { type: "h2", text: "Where Instagram is stronger" },
    {
      type: "ul",
      items: [
        "**You own the customer.** You can message, reward and sell to them again, instead of competing for the same shopper every time.",
        "**A brand, not a listing.** Customers remember your shop, not the product among thousands of near-identical listings.",
        "**Control over price and policy.** You decide your prices, your return rules and how you talk to customers.",
        "**Lower marketplace dependence.** A platform can change fees or rules, and your sales should not depend on that.",
        "**Digital products.** You can sell files and guides, which many marketplaces do not suit. See [selling digital products on Instagram](/blog/sell-digital-products-on-instagram).",
      ],
    },

    { type: "h2", text: "Can you use more than one?" },
    {
      type: "p",
      text:
        "Yes, and many sellers do. A common pattern is to use a marketplace for reach and volume while building your own audience on Instagram for repeat customers. Marketplaces generally have rules about taking customers off their platform, so read the current policy of each one and do not rely on moving buyers across.",
    },
    {
      type: "p",
      text:
        "If you do build on Instagram, the order flow matters. Our guides to [selling without losing DM orders](/blog/sell-on-instagram-dms-without-losing-orders) and [selling on Instagram without a website](/blog/sell-on-instagram-without-a-website) explain how to take orders and payments inside DMs.",
    },

    { type: "h2", text: "How to choose" },
    {
      type: "table",
      headers: ["If you...", "Start with"],
      rows: [
        ["Sell low-price items and want volume fast, and have no audience", "Meesho"],
        ["Sell branded or higher-priced items and can register for GST", "Amazon"],
        ["Have followers, make something distinctive or want repeat customers", "Instagram"],
        ["Want reach and a brand", "A marketplace plus Instagram, tracked separately"],
        ["Are unsure", "Test one product on one channel and compare the money you keep"],
      ],
    },
    {
      type: "p",
      text:
        "Whichever you pick, test it on one product first. Place an order the way a customer would, count every rupee that comes off, and see how it feels to handle returns. For a comparison with building your own store, read [Checkout DM vs Shopify](/blog/checkout-dm-vs-shopify-instagram-sellers).",
    },
  ],
  related: [
    { to: "/pricing", title: "Checkout DM pricing", description: "Plans from Free to Pro, with the feature table." },
    { to: "/help", title: "Help Guide", description: "Every Checkout DM guide in one place." },
    { to: "/faq", title: "Frequently Asked Questions", description: "Answers on payments, shipping and returns." },
  ],
  faqs: [
    {
      q: "Is Meesho really commission-free?",
      a: "Seller guides in 2026 report that Meesho charges no commission and no listing fee. It earns from other charges, such as a shipping fee on delivered orders, return shipping, GST on those fees and penalties, so the real cost is not zero.",
    },
    {
      q: "Do I need GST to sell on Meesho or Amazon?",
      a: "Guides report that Meesho allows many sellers to start without GST in most categories, while Amazon generally requires GST registration. Rules depend on your products and turnover, so check each platform\u2019s current requirements.",
    },
    {
      q: "Is Instagram better than a marketplace?",
      a: "It depends on what you need. A marketplace gives you built-in buyers but less control and fees on every order. Instagram gives you control and your own customers, but you must build an audience. Many sellers use both.",
    },
    {
      q: "Does Checkout DM charge a commission?",
      a: "No. Checkout DM does not charge a commission or transaction fee on your orders. You pay your plan price, and your payment gateway charges its own fee.",
    },
  ],
};

export default post;
